import { LocalDate } from "@js-joda/core";
import React from "react";
import { API, BodyOf, Messages, ResponseOf } from "../../core/api";
import { runGuardedByAiWriteRule, takeBlockedAiWrites } from "../../core/ai-write-guard";
import { CaregiverId, PatientId } from "../../shared/schema/schema";
import useApi from "../../shared/hooks/useApi";
import {
  getAiCapability,
  getAiCapabilityManifest,
  getAiCapabilityParamOptions,
  getAiContextEntities,
  getAiCurrentPage,
  waitForAiCapability,
  waitForAiCapabilityParamOptions,
} from "./ai-capability-registry";
import {
  AiCapabilityArgs,
  AiCapabilityParam,
  AiCapabilityParamOption,
  AiCapabilityResultRow,
} from "./ai-capability.types";
import { useAiOmnibarPlanMutation, useAiOmnibarValueSearchMutation } from "./ai-omnibar.mutations";
import {
  AiOmnibarEntry,
  AiOmnibarEntryDraft,
  AiOmnibarStepStatus,
  appendAiOmnibarEntry,
  clearAiOmnibarContext,
  clearAiOmnibarEntries,
  getAiOmnibarContext,
  getAiOmnibarEntries,
  getAiOmnibarProviderOverride,
  recordAiOmnibarTurn,
  subscribeToAiOmnibarEntries,
  updateAiOmnibarStep,
} from "./ai-omnibar.store";

/** How long to wait for a step's component to mount after the step before it opened it. */
const CAPABILITY_WAIT_MS = 10_000;
/** How long a step waits for the lists it filters by, which arrive with the page's first fetch. */
const PARAM_OPTIONS_WAIT_MS = 5_000;

export type { AiOmnibarEntry };

type RefResolution =
  | { type: "resolved"; id: number; label: string }
  | { type: "failed"; message: string };

/**
 * What to put in front of the person when something throws.
 *
 * A capability that wants to tell them something writes it itself - "Nothing on this page has that
 * status", "Could not read the date" - and those are plain Errors, so they are shown. Everything
 * else is the code breaking: a TypeError from a mistyped method, an AxiosError carrying a URL, the
 * write guard naming an endpoint. None of that means anything to the person reading a search bar,
 * so it goes to the console and they get a plain sentence saying the step did not run.
 */
function messageFor(error: unknown, fallback: string): string {
  if (error instanceof Error && error.constructor === Error && error.message.trim() !== "") {
    return error.message;
  }

  console.error("[ai-omnibar] step failed:", error);

  return fallback;
}

export function useAiOmnibar() {
  const { api } = useApi();
  const planMutation = useAiOmnibarPlanMutation();
  const valueSearchMutation = useAiOmnibarValueSearchMutation();
  const searchValues: ValueSearch = valueSearchMutation.mutateAsync;

  const entries = React.useSyncExternalStore(subscribeToAiOmnibarEntries, getAiOmnibarEntries);
  const [isRunning, setIsRunning] = React.useState(false);

  const append = appendAiOmnibarEntry;
  const updateStep = updateAiOmnibarStep;
  const clear = React.useCallback(() => {
    clearAiOmnibarEntries();
    clearAiOmnibarContext();
  }, []);

  const submit = React.useCallback(
    async (text: string) => {
      const trimmed = text.trim();

      if (trimmed === "" || isRunning) {
        return;
      }

      // Only the turns since the last unrelated request - see the store. Step lines never go: they
      // are UI detail the model did not write.
      const history = getAiOmnibarContext();

      append({ kind: "user", text: trimmed });
      setIsRunning(true);

      try {
        const plan = await planMutation.mutateAsync({
          provider: getAiOmnibarProviderOverride(),
          text: trimmed,
          capabilities: getAiCapabilityManifest(),
          context: {
            currentPage: getAiCurrentPage(),
            openEntities: getAiContextEntities(),
            today: LocalDate.now().toString(),
          },
          history,
        });

        append({ kind: "assistant", text: plan.reply });

        // The model judged whether this carried on from the window it was given; that decides what
        // the next request is allowed to see.
        recordAiOmnibarTurn({
          request: trimmed,
          reply: plan.reply,
          continuesPrevious: plan.continuesPrevious,
        });

        // Shared for the whole request: the first step to arrive on a screen resolves the words of
        // every later step on that same screen too, so one arrival is one value search.
        const resolvedWords: ResolvedWords = new Map();

        for (const [index, step] of plan.steps.entries()) {
          const ok = await runStep(api, step, append, updateStep, searchValues, {
            laterSteps: plan.steps.slice(index + 1),
            resolvedWords,
          });

          if (ok) {
            continue;
          }

          // A step that failed only matters to the ones that were waiting on it. Three filters on one
          // report do not need each other, so a value that could not be placed should not silently
          // cancel the other two; a step that failed to *open* something does stop them, because they
          // are not available and never will be.
          const restAreReady = plan.steps
            .slice(index + 1)
            .every((later) => getAiCapability(later.capabilityId)?.run !== undefined);

          if (!restAreReady) {
            break;
          }
        }
      } catch (error) {
        append({
          kind: "note",
          text: messageFor(error, "The assistant could not be reached."),
        });
      } finally {
        setIsRunning(false);
      }
    },
    [api, append, isRunning, planMutation, searchValues, updateStep]
  );

  /**
   * Runs a result the user clicked. Goes through the registry and the write guard exactly like a
   * step the model planned, so a result can never do more than a capability could.
   */
  const runResultRow = React.useCallback(
    async (row: AiCapabilityResultRow) => {
      const clicked = getAiCapability(row.capabilityId);
      const run = clicked?.run;

      if (clicked === undefined || run === undefined) {
        append({ kind: "note", text: `"${row.capabilityId}" is not available right now.` });
        return;
      }

      const entryId = append({ kind: "step", text: `Opening ${row.label}`, status: "running" });

      try {
        const outcome = await runGuardedByAiWriteRule(
          () => run(row.args, { labels: {} }),
          clicked.effect
        );
        const text = typeof outcome === "string" ? outcome : outcome?.text;

        updateStep(entryId, { status: "done", text: text === "" ? undefined : text });
      } catch (error) {
        updateStep(entryId, { status: "failed" });
        append({
          kind: "note",
          text: messageFor(error, `Could not open ${row.label}.`),
        });
      }

      reportBlockedWrites(row.capabilityId, append);
    },
    [append, updateStep]
  );

  /**
   * The conversation split into turns - one user query and everything that answered it. The panel
   * shows only the last one; the store keeps the rest so the model still has context for follow-ups
   * like "the other one".
   */
  const turns = React.useMemo(() => groupIntoTurns(entries), [entries]);

  return { entries, turns, isRunning, submit, clear, runResultRow };
}

export type AiOmnibarTurn = {
  /** Id of the user entry that opened the turn, so React can key on it. */
  id: number;
  entries: AiOmnibarEntry[];
};

function groupIntoTurns(entries: AiOmnibarEntry[]): AiOmnibarTurn[] {
  const turns: AiOmnibarTurn[] = [];

  for (const entry of entries) {
    if (entry.kind === "user" || turns.length === 0) {
      turns.push({ id: entry.id, entries: [entry] });
      continue;
    }

    turns[turns.length - 1].entries.push(entry);
  }

  return turns;
}

type ValueSearchBody = BodyOf<"post", "./ai_omnibar/value_search">;

type ValueSearch = (
  body: ValueSearchBody
) => Promise<ResponseOf<"post", "./ai_omnibar/value_search">>;

async function runStep(
  api: API,
  step: Messages["AiOmnibarPlanStep"],
  append: (entry: AiOmnibarEntryDraft) => number,
  updateStep: (id: number, patch: { status: AiOmnibarStepStatus; text?: string }) => void,
  searchValues: ValueSearch,
  batch: { laterSteps: Messages["AiOmnibarPlanStep"][]; resolvedWords: ResolvedWords }
): Promise<boolean> {
  const capability = getAiCapability(step.capabilityId);

  if (capability === undefined) {
    append({ kind: "note", text: `"${step.capabilityId}" is not part of this screen.` });
    return false;
  }

  // The plan no longer writes a line per step: it was replaced by the runner's own the moment the
  // step finished, so it was output the model composed and nobody read. The capability's own title
  // says the same thing for the second it is on screen.
  // The title plus what was asked for, which is as much as the model's discarded narration said and
  // costs nothing to produce. Replaced by the runner's own line the moment the step finishes.
  const asked = [...step.params.map((param) => param.value), ...(step.searchTerms ?? [])]
    .filter((value) => value.trim() !== "")
    .join(", ");

  const entryId = append({
    kind: "step",
    text:
      step.narration !== ""
        ? step.narration
        : asked === ""
        ? capability.title
        : `${capability.title}: ${asked}`,
    status: "running",
  });

  // Waiting comes before the params are read, not after: a step opened by the step before it has to
  // be mounted for its own option lists to exist at all - the claims page's contracts, the table's
  // columns. This is the whole reason values are matched here rather than at planning time.
  if (capability.run === undefined) {
    const becameAvailable = await waitForAiCapability(capability.id, CAPABILITY_WAIT_MS);

    if (!becameAvailable) {
      updateStep(entryId, { status: "failed" });
      append({ kind: "note", text: `${capability.title} did not open in time.` });
      return false;
    }
  }

  // Being mounted is not the same as being loaded. A page registers its filters as it renders and
  // fills their dropdowns from the rows it then fetches, so a step landing behind a navigation has
  // to wait for the lists themselves - otherwise a value the page does hold reads as one it never
  // had. Nothing to wait for when the params are dates or free text, or the lists are already full.
  await waitForAiCapabilityParamOptions(
    capability.id,
    step.params.map((param) => param.name),
    PARAM_OPTIONS_WAIT_MS
  );

  const args: AiCapabilityArgs = {};
  const labels: Record<string, string> = {};

  const stepParams = step.params.flatMap((param) => {
    const spec = (capability.params ?? []).find((candidate) => candidate.name === param.name);

    // The server drops unknown capability ids; unknown param names are dropped here.
    return spec === undefined ? [] : [{ spec, requested: param.value }];
  });

  // Said out loud rather than dropped quietly. A plan that invents a field - a "search" box the
  // screen does not declare - otherwise looks like it applied a filter it never applied, and the
  // only clue is a result set that is wider than the user asked for.
  const unknownParams = step.params
    .filter((param) => !(capability.params ?? []).some((spec) => spec.name === param.name))
    .map((param) => `${param.name} "${param.value}"`);

  if (unknownParams.length > 0) {
    append({
      kind: "note",
      text: `${capability.title} has no ${unknownParams.join(" or ")}, so I left it out.`,
    });
  }

  // Everything the rest of this plan will need from this same screen, asked for now while we are
  // here. Their own steps then find the answers waiting for them.
  const laterWords = batch.laterSteps
    .filter((later) => later.capabilityId === step.capabilityId)
    .flatMap((later) => [
      // Only the params that are closed lists. A later step's free text - the value typed into a
      // filter - has no list to be found in, and asking about it just spends prompt on being told so.
      ...later.params
        .filter((param) => getAiCapabilityParamOptions(step.capabilityId, param.name).length > 0)
        .map((param) => param.value),
      ...(later.searchTerms ?? []),
    ]);

  const resolved = await searchStepValues(
    capability,
    stepParams,
    step.searchTerms ?? [],
    searchValues,
    { laterWords, resolvedWords: batch.resolvedWords }
  );

  if (resolved.type === "ambiguous") {
    updateStep(entryId, { status: "failed" });
    append({ kind: "note", text: resolved.message });
    // Clicking one comes back through the same capability, so a choice can do no more than the step
    // it stood in for. Params the plan gave as plain text ride along; a reference would not survive
    // this, and no capability has both.
    const base: AiCapabilityArgs = {};

    for (const { spec, requested } of stepParams) {
      if (
        spec.name !== resolved.param &&
        getAiCapabilityParamOptions(capability.id, spec.name).length === 0
      ) {
        base[spec.name] = requested;
      }
    }

    append({
      kind: "results",
      text: resolved.message,
      rows: resolved.options.map((option) => ({
        label: option.label,
        capabilityId: capability.id,
        args: { ...base, [resolved.param]: option.value },
      })),
    });

    return false;
  }

  if (resolved.type === "failed") {
    updateStep(entryId, { status: "failed" });
    append({ kind: "note", text: resolved.message });
    return false;
  }

  if (resolved.dropped.length > 0) {
    append({
      kind: "note",
      text: `I left out ${resolved.dropped
        .map((term) => `"${term}"`)
        .join(" and ")} - nothing on this screen takes it.`,
    });
  }

  // Built from the resolution, not from the plan. A closed-list param only ever gets a value the
  // screen offered, and a value rescued into a different field than the plan chose has to land in
  // that field - so the two are gathered separately and merged here.
  const toApply = new Map<
    string,
    { spec: AiCapabilityParam; value: string | string[]; label?: string }
  >();

  for (const { spec, requested } of stepParams) {
    if (resolved.omit.has(spec.name)) {
      // A list claimed this word; typing it into the search box as well would filter it away.
      continue;
    }

    if (getAiCapabilityParamOptions(capability.id, spec.name).length > 0) {
      // A closed list is applied from the resolution below, never from the raw words: passing
      // "europe" straight to a field that has no such option is how a step ends up reporting the
      // component's own "no such contract" error instead of what actually happened.
      continue;
    }

    toApply.set(spec.name, { spec, value: requested });
  }

  for (const [name, field] of resolved.values) {
    const spec = (capability.params ?? []).find((candidate) => candidate.name === name);

    if (spec === undefined) {
      continue;
    }

    toApply.set(name, {
      spec,
      // One value stays a value; a multiselect gets the list, so "france and germany" arrives as
      // both rather than as one contract and a complaint.
      value:
        spec.multiple === true ? field.values.map((value) => value.value) : field.values[0].value,
      label: field.values.map((value) => value.label).join(", "),
    });
  }

  for (const { spec, value, label } of toApply.values()) {
    // A list is coerced entry by entry - each is one option value of the same field. The type is
    // spelled out because the root tsconfig has strictNullChecks off, where an empty literal is
    // never[] rather than an array that learns what it holds.
    const coercedEach: Extract<CoercedParam, { type: "ok" }>[] = [];

    for (const one of Array.isArray(value) ? value : [value]) {
      const coerced = await coerceParam(api, spec, one);

      if (coerced.type === "failed") {
        updateStep(entryId, { status: "failed" });
        append({ kind: "note", text: coerced.message });
        return false;
      }

      coercedEach.push(coerced);
    }

    const coerced = coercedEach[0];

    args[spec.name] = Array.isArray(value)
      ? coercedEach.map((entry) => entry.value as string | number)
      : coerced.value;

    const resolvedLabel = coerced.label ?? label;

    if (resolvedLabel !== undefined) {
      labels[spec.name] = resolvedLabel;
    }
  }

  const missing = (capability.params ?? [])
    .filter((param) => (param.required ?? false) && args[param.name] === undefined)
    .map((param) => param.name);

  if (missing.length > 0) {
    updateStep(entryId, { status: "failed" });
    append({ kind: "note", text: `I still need: ${missing.join(", ")}.` });
    return false;
  }

  const run = getAiCapability(capability.id)?.run;

  if (run === undefined) {
    updateStep(entryId, { status: "failed" });
    return false;
  }

  try {
    // Capabilities run inside the write guard: navigating and prefilling is allowed, anything
    // that would change stored state is refused before it reaches the API.
    const outcome = await runGuardedByAiWriteRule(() => run(args, { labels }), capability.effect);

    const text = typeof outcome === "string" ? outcome : outcome?.text;
    const results = typeof outcome === "object" && outcome !== null ? outcome.results : undefined;

    updateStep(entryId, {
      status: "done",
      text: text !== undefined && text !== "" ? text : undefined,
    });

    // A capability that found several things offers them for the user to act on, rather than
    // guessing. Clicking one is just another capability call - see runResultRow.
    if (results !== undefined && results.length > 0) {
      append({
        kind: "results",
        text: `${results.length} result${results.length === 1 ? "" : "s"}`,
        rows: results,
      });
    }

    reportBlockedWrites(capability.title, append);

    return true;
  } catch (error) {
    updateStep(entryId, { status: "failed" });
    append({
      kind: "note",
      text: messageFor(error, `${capability.title} could not run.`),
    });

    reportBlockedWrites(capability.title, append);

    return false;
  }
}

/**
 * A blocked request means a capability tried to change state, which it is not allowed to do. The
 * guard already refused it; this makes sure the user sees that it happened rather than wondering
 * why a screen looks half done.
 */
function reportBlockedWrites(
  capabilityTitle: string,
  append: (entry: AiOmnibarEntryDraft) => number
): void {
  const blocked = takeBlockedAiWrites();

  if (blocked.length === 0) {
    return;
  }

  // The endpoints go to the console, not to the search bar: which POST was refused matters to
  // whoever is fixing it and means nothing to the person who just asked to see a page.
  console.error(
    "[ai-omnibar] blocked writes from",
    capabilityTitle,
    blocked.map((write) => `${write.method} ${write.path}`).join(", ")
  );

  append({
    kind: "note",
    text: `"${capabilityTitle}" tried to change data and was blocked - the assistant may only open and prefill screens.`,
  });
}

type CoercedParam =
  | { type: "ok"; value: string | number | boolean; label?: string }
  | { type: "failed"; message: string };

type StepParam = { spec: AiCapabilityParam; requested: string };

type MatchedValue = { param: string; value: string; label: string };

/** Every value placed on one field. More than one only for a param declared `multiple`. */
type MatchedField = { param: string; values: MatchedValue[] };

/**
 * What the value search has already answered, for this request, keyed by capability and word.
 *
 * Filled by the first step that arrives on a screen - for every word the plan will use there, not
 * just its own - so four filters on one report cost one call rather than four. A null answer is
 * cached too: asking again would only spend another second to be told the same thing.
 */
type ResolvedWords = Map<string, MatchedValue | null>;

const resolvedWordKey = (capabilityId: string, requested: string) =>
  `${capabilityId}|${normaliseOptionTerm(requested)}`;

type StepValues =
  | {
      type: "ok";
      values: Map<string, MatchedField>;
      /** Params the plan filled that a list has since claimed, so they must not also be applied. */
      omit: Set<string>;
      /** Words the plan threw in without naming a field, which nothing on the screen could take. */
      dropped: string[];
    }
  | { type: "failed"; message: string }
  | {
      /**
       * Several options fit the word and nothing could choose between them. Not a failure: the
       * candidates are worth offering, because the person knows which one they meant.
       */
      type: "ambiguous";
      message: string;
      param: string;
      options: AiCapabilityParamOption[];
    };

/** Loose enough to match "France" to "france" or "EDI - eMedNY" to "edi emedny". */
function normaliseOptionTerm(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9]/g, "");
}

/**
 * The same, but keeping word boundaries, padded so a containment test cannot straddle one.
 *
 * Stripping the spaces made "unpaid" contain "paid" - which quietly applied the opposite filter to
 * the one asked for. Whole words only: " unpaid " does not contain " paid ".
 */
function normaliseOptionWords(text: string): string {
  return ` ${text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim()} `;
}

/**
 * Whether one padded word string covers the other: the same words, in the same order, with anything
 * allowed in between.
 *
 * Plain containment is not enough for the hierarchical labels a navigation destination carries.
 * "authorization utilization patients" is exactly what " patients authorization utilization beta
 * patients " offers, but not as one unbroken run of words. Order still has to hold, so it does not
 * also match "patients authorization", and a single word still has to be contained outright - one
 * word skipping through a long label matches almost anything.
 */
function coversWords(haystack: string, needle: string): boolean {
  if (haystack.includes(needle)) {
    return true;
  }

  const words = needle
    .trim()
    .split(" ")
    .filter((word) => word !== "");

  if (words.length < 2) {
    return false;
  }

  let from = 0;

  return words.every((word) => {
    const at = haystack.indexOf(` ${word} `, from);

    if (at === -1) {
      return false;
    }

    from = at + word.length + 1;

    return true;
  });
}

/**
 * Turns the words in a planned step into values the mounted component will accept.
 *
 * The plan speaks the user's language - "france", "europe", "unpaid" - and at best guesses which
 * field each word belongs to, because it was never shown a single value. So the search is over every
 * closed list on this screen, not just the field the plan named: a word the plan filed under contract
 * lands on office if that is where it really lives.
 *
 * Exact hits are found here and cost nothing. Only the leftovers go to the model, with just this
 * screen's lists - a request about one contract does not carry the other four hundred.
 */
/**
 * The one place that asks the model to place words, and the only place that fills the cache.
 *
 * Every field on the screen goes with the question, whatever the plan guessed, because a word the
 * plan filed under one field often belongs to another. Answers are written to the cache - including
 * the ones that matched nothing, so a later step does not pay to be told the same.
 */
async function askValueSearch(params: {
  capability: { id: string; title: string };
  fields: ValueSearchBody["fields"];
  terms: ValueSearchBody["terms"];
  searchValues: ValueSearch;
  resolvedWords: ResolvedWords;
}): Promise<void> {
  const { capability, fields, terms, searchValues, resolvedWords } = params;

  const result = await searchValues({
    provider: getAiOmnibarProviderOverride(),
    capabilityId: capability.id,
    capabilityTitle: capability.title,
    terms,
    fields,
  });

  for (const term of terms) {
    const match = result.matches.find(
      (candidate: { requested: string }) => candidate.requested === term.requested
    );

    resolvedWords.set(
      resolvedWordKey(capability.id, term.requested),
      match === undefined || match.param === null || match.value === null
        ? null
        : { param: match.param, value: match.value, label: match.label ?? match.value }
    );
  }
}

async function searchStepValues(
  capability: { id: string; title: string; params?: AiCapabilityParam[] },
  stepParams: StepParam[],
  /** Words the plan could not place in a field - see AiOmnibarPlanStep.searchTerms. */
  looseTerms: string[],
  searchValues: ValueSearch,
  batch: { laterWords: string[]; resolvedWords: ResolvedWords }
): Promise<StepValues> {
  const values = new Map<string, MatchedField>();
  const omit = new Set<string>();

  const takesSeveral = (param: string) =>
    (capability.params ?? []).find((candidate) => candidate.name === param)?.multiple === true;

  /**
   * Records a value against its field. A multiselect keeps them all, in the order they were asked
   * for; anything else keeps the first and lets the caller report the rest as left out.
   */
  const place = (match: MatchedValue): boolean => {
    const existing = values.get(match.param);

    if (existing === undefined) {
      values.set(match.param, { param: match.param, values: [match] });

      return true;
    }

    if (!takesSeveral(match.param)) {
      return false;
    }

    if (existing.values.some((value) => value.value === match.value)) {
      return true;
    }

    existing.values.push(match);

    return true;
  };

  /** True once a single-value field is taken, so later words look elsewhere for a home. */
  const isTaken = (param: string) => values.has(param) && !takesSeveral(param);

  // Every closed list on this screen, whether or not the plan mentioned it.
  const fields = (capability.params ?? [])
    .map((param) => ({
      param: param.name,
      description: param.description,
      options: getAiCapabilityParamOptions(capability.id, param.name),
    }))
    .filter((field) => field.options.length > 0);

  if (fields.length === 0) {
    return { type: "ok", values, omit, dropped: [] };
  }

  const fieldNames = new Set(fields.map((field) => field.param));
  const terms: ValueSearchBody["terms"] = [];
  /** Words that fitted more than one option locally, kept in case nothing else places them. */
  const nearMisses = new Map<string, { param: string; option: AiCapabilityParamOption }[]>();

  // A word the plan typed into the screen's search box is checked against the lists too. It is a
  // real choice the planner makes - "in europe" reads like something to search for - but if a list
  // holds that exact name, the filter is what the user meant. Local matching only: a word that
  // needs a model to place it is a phrase like "winthrop street", which belongs in the search box.
  const typedFreeText = stepParams.filter(
    (stepParam) => stepParam.spec.searchesFreeText === true && stepParam.requested.trim() !== ""
  );

  for (const { spec, requested } of typedFreeText) {
    const term = normaliseOptionTerm(requested);
    const hit = fields
      .flatMap((field) =>
        field.options
          .filter((option) => normaliseOptionTerm(option.label) === term)
          .map((option) => ({ param: field.param, option }))
      )
      .at(0);

    if (hit !== undefined) {
      place({ param: hit.param, value: hit.option.value, label: hit.option.label });
      omit.add(spec.name);
    }
  }

  const wanted = [
    ...stepParams
      .filter((stepParam) => fieldNames.has(stepParam.spec.name))
      .map((stepParam) => ({
        requested: stepParam.requested,
        preferredParam: stepParam.spec.name,
      })),
    ...looseTerms
      .filter((requested) => requested.trim() !== "")
      .map((requested) => ({ requested, preferredParam: null })),
  ];

  for (const { requested, preferredParam } of wanted) {
    const term = normaliseOptionTerm(requested);
    // The plan's own field first, then the rest: when the word really is a value of the field the
    // plan named, that is the answer, and no call is needed. A loose term simply has no first field.
    const ordered = [
      ...fields.filter((field) => field.param === preferredParam),
      ...fields.filter((field) => field.param !== preferredParam),
    ];

    const searchable = ordered.filter((field) => !isTaken(field.param));

    const exact = searchable
      .flatMap((field) =>
        field.options
          .filter(
            (option) =>
              option.value === requested ||
              normaliseOptionTerm(option.value) === term ||
              normaliseOptionTerm(option.label) === term
          )
          .map((option) => ({ param: field.param, option }))
      )
      .at(0);

    // Then a shortening of one label and nothing else's: "weekly hours" is Weekly Hours For Patient,
    // and no other report contains those words in that order. Only taken when it picks out exactly
    // one option across every field - two candidates is what the value search is for, and a term
    // this short is too easy to match by accident.
    const CONTAINS_MIN_LENGTH = 4;

    const words = normaliseOptionWords(requested);

    const containing =
      exact !== undefined || term.length < CONTAINS_MIN_LENGTH
        ? []
        : searchable.flatMap((field) =>
            field.options
              .filter((option) => {
                const label = normaliseOptionWords(option.label);

                return coversWords(label, words) || coversWords(words, label);
              })
              .map((option) => ({ param: field.param, option }))
          );

    const hit = exact ?? (containing.length === 1 ? containing[0] : undefined);

    if (hit === undefined && containing.length > 1) {
      nearMisses.set(requested, containing);
    }

    if (hit !== undefined) {
      place({ param: hit.param, value: hit.option.value, label: hit.option.label });
      continue;
    }

    terms.push({ requested, preferredParam });
  }

  // Anything a previous step on this screen already had answered.
  const stillUnknown = terms.filter(
    (term) => !batch.resolvedWords.has(resolvedWordKey(capability.id, term.requested))
  );

  // The words later steps on this screen will need, asked for in the same breath.
  const lookahead: ValueSearchBody["terms"] = batch.laterWords
    .filter((word) => word.trim() !== "")
    .filter((word) => !batch.resolvedWords.has(resolvedWordKey(capability.id, word)))
    .filter((word) => !terms.some((term) => term.requested === word))
    .filter((word, index, all) => all.findIndex((other) => other === word) === index)
    .map((word) => ({ requested: word, preferredParam: null }));

  if (terms.length === 0) {
    return { type: "ok", values, omit, dropped: [] };
  }

  if (stillUnknown.length > 0 || lookahead.length > 0) {
    await askValueSearch({
      capability,
      // A field this step has already matched locally is left out, so one word cannot displace
      // another. The rest go, including the ones only a later step will use.
      fields: fields.filter((field) => !isTaken(field.param)),
      terms: [...stillUnknown, ...lookahead],
      searchValues,
      resolvedWords: batch.resolvedWords,
    });
  }

  const unmatched: string[] = [];
  /** Unmatched words the plan had named a field for - see the fallback below. */
  const unmatchedNamedField: string[] = [];

  for (const term of terms) {
    const match = batch.resolvedWords.get(resolvedWordKey(capability.id, term.requested));

    if (match === undefined || match === null || !place(match)) {
      unmatched.push(term.requested);

      if (term.preferredParam !== null) {
        unmatchedNamedField.push(term.requested);
      }

      continue;
    }
  }

  if (unmatched.length > 0) {
    // A word in none of the lists is not necessarily a mistake - most screens have a search box that
    // covers names, addresses and ids, and a component says which param that is.
    //
    // But only for a word nobody claimed to know the field of. "the spain contract" named its field,
    // so there being no Spain contract is the answer; typing it into the search box instead would
    // narrow the table to nothing and report that as success.
    const freeText =
      unmatchedNamedField.length > 0
        ? undefined
        : (capability.params ?? []).find((param) => param.searchesFreeText === true);
    const alreadyTyped = stepParams.some(
      (stepParam) => stepParam.spec.name === freeText?.name && stepParam.requested.trim() !== ""
    );

    if (freeText !== undefined && !alreadyTyped) {
      place({
        param: freeText.name,
        value: unmatched.join(" "),
        label: `"${unmatched.join(" ")}" in the search box`,
      });

      return { type: "ok", values, omit, dropped: [] };
    }

    // A word that fitted several options and could not be narrowed is a question, not a dead end -
    // "weekly hours report" really is two of this agency's reports.
    const contested = unmatched
      .map((term) => ({ term, candidates: nearMisses.get(term) ?? [] }))
      .find((entry) => entry.candidates.length > 1);

    if (contested !== undefined) {
      return {
        type: "ambiguous",
        message: `More than one matches "${contested.term}". Which one?`,
        param: contested.candidates[0].param,
        options: contested.candidates.map((candidate) => candidate.option),
      };
    }

    // A word the plan named a field for is a promise it could not keep, so the step stops. A word it
    // volunteered without naming a field is a hint - "france" thrown in beside an explicit
    // field/value pair - and dropping a hint is not worth cancelling the work that did resolve.
    if (unmatchedNamedField.length === 0) {
      // A hint that repeats something the step already applied is not worth mentioning: the plan
      // often sends "france" both as the value of a field and again as a loose word, and reporting
      // that as left out reads as a filter having been missed when it was not.
      //
      // Both halves count as applied: the labels of the lists that matched, and the words the plan
      // put in the step's own plain params - which is where "france" actually landed, since a free
      // text value never passes through a list.
      const applied = [
        ...[...values.values()].flatMap((field) =>
          field.values.map((value) => normaliseOptionWords(value.label))
        ),
        ...stepParams
          .filter((stepParam) => !fieldNames.has(stepParam.spec.name))
          .map((stepParam) => normaliseOptionWords(stepParam.requested)),
      ];

      return {
        type: "ok",
        values,
        omit,
        dropped: unmatched.filter((term) => {
          const words = normaliseOptionWords(term);

          return !applied.some((label) => label.includes(words) || words.includes(label));
        }),
      };
    }

    // Nothing is applied when a word cannot be found and there is nowhere to type it: a search run
    // with the rest of the filters would look like it answered the question, and it did not.
    return {
      type: "failed",
      message: `Nothing on this screen matches ${unmatchedNamedField
        .map((term) => `"${term}"`)
        .join(" or ")}.`,
    };
  }

  return { type: "ok", values, omit, dropped: [] };
}

async function coerceParam(api: API, spec: AiCapabilityParam, raw: string): Promise<CoercedParam> {
  switch (spec.type) {
    case "number": {
      const value = Number(raw);

      return Number.isFinite(value)
        ? { type: "ok", value }
        : { type: "failed", message: `"${raw}" is not a number for ${spec.name}.` };
    }

    case "boolean":
      return { type: "ok", value: raw.toLowerCase() === "true" };

    case "date":
      return /^\d{4}-\d{2}-\d{2}$/.test(raw)
        ? { type: "ok", value: raw }
        : { type: "failed", message: `I could not read "${raw}" as a date.` };

    case "patientRef": {
      const resolution = await resolvePatientRef(api, raw);

      return resolution.type === "resolved"
        ? { type: "ok", value: resolution.id, label: resolution.label }
        : { type: "failed", message: resolution.message };
    }

    case "caregiverRef": {
      const resolution = await resolveCaregiverRef(api, raw);

      return resolution.type === "resolved"
        ? { type: "ok", value: resolution.id, label: resolution.label }
        : { type: "failed", message: resolution.message };
    }

    case "invoiceRef": {
      const resolution = await resolveInvoiceRef(api, raw);

      return resolution.type === "resolved"
        ? { type: "ok", value: resolution.id, label: resolution.label }
        : { type: "failed", message: resolution.message };
    }

    case "text":
    case "time":
    case "enum":
      return { type: "ok", value: raw };
  }
}

async function resolvePatientRef(api: API, raw: string): Promise<RefResolution> {
  const term = raw.trim();

  // The id fields are mapped numeric in the search index, and a multi_match that mixes them with
  // text fields throws for a non-numeric term - failing every shard, so the whole search comes back
  // empty. Ask for the id fields only when the term could actually be one.
  const isNumeric = /^\d+$/.test(term);

  const response = await api.get("/agencies/:agencyId/search", {
    query: {
      text: term,
      patient_id: isNumeric,
      patient_first_name: !isNumeric,
      patient_middle_name: !isNumeric,
      patient_last_name: !isNumeric,
    },
  });

  const label = (patient: Messages["GlobalSearchPatientResult"]) =>
    `${patient.firstName} ${patient.lastName}${
      patient.displayId === null ? "" : ` (#${patient.displayId})`
    }`;

  const match = pickMatch({
    term,
    entity: "patient",
    results: response.patients.map((patient) => ({
      id: patient.id,
      displayId: patient.displayId,
      name: `${patient.firstName} ${patient.lastName}`,
      label: label(patient),
    })),
  });

  if (match.type === "resolved" || !/^\d+$/.test(term)) {
    return match;
  }

  // The agency search is text based, so a short internal id like "1" matches nothing there even
  // though the patient exists. Ask for that patient directly before giving up.
  const direct = await fetchPatientById(api, Number(term));

  return direct ?? match;
}

async function fetchPatientById(api: API, id: number): Promise<RefResolution | null> {
  try {
    const patient = await api.get("/agencies/:agencyId/patients/:patientId", {
      path: { patientId: id as PatientId },
    });

    return {
      type: "resolved",
      id: patient.id,
      label: `${patient.firstName} ${patient.lastName}${
        patient.displayId === null ? "" : ` (#${patient.displayId})`
      }`,
    };
  } catch {
    // No such patient - keep the message the search produced.
    return null;
  }
}

async function resolveCaregiverRef(api: API, raw: string): Promise<RefResolution> {
  const term = raw.trim();

  // Same reason as for patients: never mix the numeric id fields with a name.
  const isNumeric = /^\d+$/.test(term);

  const response = await api.get("/agencies/:agencyId/search", {
    query: {
      text: term,
      caregiver_id: isNumeric,
      caregiver_first_name: !isNumeric,
      caregiver_middle_name: !isNumeric,
      caregiver_last_name: !isNumeric,
    },
  });

  const match = pickMatch({
    term,
    entity: "caregiver",
    results: response.caregivers.map((caregiver) => ({
      id: caregiver.id,
      displayId: caregiver.displayId,
      name: `${caregiver.firstName} ${caregiver.lastName}`,
      label: `${caregiver.firstName} ${caregiver.lastName}${
        caregiver.displayId === null ? "" : ` (#${caregiver.displayId})`
      }`,
    })),
  });

  if (match.type === "resolved" || !/^\d+$/.test(term)) {
    return match;
  }

  const direct = await fetchCaregiverById(api, Number(term));

  return direct ?? match;
}

async function fetchCaregiverById(api: API, id: number): Promise<RefResolution | null> {
  try {
    const caregiver = await api.get("/agencies/:agencyId/caregivers/:caregiverId", {
      path: { caregiverId: id as CaregiverId },
    });

    return {
      type: "resolved",
      id: caregiver.id,
      label: `${caregiver.firstName} ${caregiver.lastName}${
        caregiver.displayId === null ? "" : ` (#${caregiver.displayId})`
      }`,
    };
  } catch {
    return null;
  }
}

/**
 * Claims are quoted by their display id, which is a string in the search index rather than a
 * number, so this resolves on that and falls back to treating the term as an internal id.
 */
async function resolveInvoiceRef(api: API, raw: string): Promise<RefResolution> {
  const term = raw.trim();

  const response = await api.get("/agencies/:agencyId/search", {
    query: { text: term, include_invoices: true },
  });

  const exact = response.invoices.filter((invoice) => invoice.displayId === term);
  const candidates = exact.length > 0 ? exact : response.invoices;

  if (candidates.length === 1) {
    return {
      type: "resolved",
      id: candidates[0].id,
      label: `claim ${candidates[0].displayId}`,
    };
  }

  if (candidates.length > 1) {
    return {
      type: "failed",
      message: `More than one claim matches "${term}": ${candidates
        .slice(0, 4)
        .map((invoice) => invoice.displayId)
        .join(", ")}. Which one?`,
    };
  }

  // No fallback to "treat the number as an internal id" here, unlike patients: there is no
  // endpoint to check a claim exists first, and opening a modal on a made-up id would look like
  // the claim was found and then broke.
  return { type: "failed", message: `I could not find a claim matching "${term}".` };
}

function pickMatch(params: {
  term: string;
  entity: "patient" | "caregiver";
  results: { id: number; displayId: number | null; name: string; label: string }[];
}): RefResolution {
  const { term, entity, results } = params;

  if (results.length === 0) {
    return { type: "failed", message: `I could not find a ${entity} matching "${term}".` };
  }

  // "patient 2" is the id a coordinator reads off the screen, which is the display id.
  if (/^\d+$/.test(term)) {
    const asNumber = Number(term);
    const byDisplayId = results.filter((result) => result.displayId === asNumber);
    const byId = results.filter((result) => result.id === asNumber);
    const numeric = byDisplayId.length > 0 ? byDisplayId : byId;

    if (numeric.length === 1) {
      return { type: "resolved", id: numeric[0].id, label: numeric[0].label };
    }
  }

  if (results.length === 1) {
    return { type: "resolved", id: results[0].id, label: results[0].label };
  }

  const exact = results.filter((result) => result.name.toLowerCase() === term.toLowerCase());

  if (exact.length === 1) {
    return { type: "resolved", id: exact[0].id, label: exact[0].label };
  }

  const candidates = results
    .slice(0, 4)
    .map((result) => result.label)
    .join(", ");

  return {
    type: "failed",
    message: `More than one ${entity} matches "${term}": ${candidates}. Which one?`,
  };
}
